import { captureAuthSessionOwner, isAuthSessionOwnerCurrent } from "@/shared/auth/session";
import { currentAccountStorageScope } from "@/shared/storage/account-scope";
import { attestsExactDashboardBriefingV2Plan } from "./dashboard-briefing-v2.mjs";
import { boundedRetainedAudio, retainedAudioIsEligible, RETAINED_AUDIO_MAX_AGE_MS, RETAINED_AUDIO_MAX_BYTES } from "./retained-audio-policy.mjs";
import { clearRetainedAudioCheckpointForScope } from "./retained-audio-resume";
import type { AudioResponse } from "./audio-types";

export type RetainedAudioRecording = {
  version: 1;
  id: string;
  ownerScope: string;
  kind: "brief" | "news";
  label: string;
  detail: string;
  language: string;
  savedAtEpochMs: number;
  expiresAtEpochMs: number;
  durationMs: number;
  byteLength: number;
  sections: Array<{ id: string; title: string; startMs: number; endMs: number }>;
};
type StoredRecording = RetainedAudioRecording & { storageKey: string; blob: Blob };
export type RetainedAudioOwner = ReturnType<typeof captureRetainedAudioOwner>;
const changes = new EventTarget();
const DATABASE = "tb_retained_brief_audio_v1";
const STORE = "recordings";
let ownerGeneration = 0;
let observedOwnerIdentity = "";
function observeOwnerIdentity() {
  const auth = captureAuthSessionOwner();
  const identity = `${currentAccountStorageScope()}:${auth?.userId || ""}:${auth?.sessionId || ""}`;
  if (identity !== observedOwnerIdentity) { observedOwnerIdentity = identity; ownerGeneration += 1; }
}
if (typeof window !== "undefined") {
  window.addEventListener("tb-auth-changed", observeOwnerIdentity);
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key === "tb_auth_session_v1") { ownerGeneration += 1; observeOwnerIdentity(); }
  });
}

export function captureRetainedAudioOwner() {
  observeOwnerIdentity();
  return { scope: currentAccountStorageScope(), auth: captureAuthSessionOwner(), generation: ownerGeneration };
}
export function retainedAudioOwnerIsCurrent(owner: RetainedAudioOwner): boolean {
  observeOwnerIdentity();
  return owner.generation === ownerGeneration && owner.scope === currentAccountStorageScope() && (owner.auth
    ? isAuthSessionOwnerCurrent(owner.auth) : captureAuthSessionOwner() === null);
}
export function onRetainedAudioChanged(listener: () => void): () => void {
  changes.addEventListener("change", listener);
  return () => changes.removeEventListener("change", listener);
}
function changed() { changes.dispatchEvent(new Event("change")); }
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("audio_storage_unavailable")); return; }
    const request = indexedDB.open(DATABASE, 1);
    let blocked = false;
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore(STORE, { keyPath: "storageKey" });
      store.createIndex("ownerScope", "ownerScope");
    };
    request.onsuccess = () => { if (blocked) request.result.close(); else resolve(request.result); };
    request.onerror = () => reject(new Error("audio_storage_unavailable"));
    request.onblocked = () => { blocked = true; reject(new Error("audio_storage_unavailable")); };
  });
}
async function mutateOwner<T>(owner: RetainedAudioOwner, mutate: (store: IDBObjectStore, rows: StoredRecording[]) => T): Promise<T> {
  const database = await openDatabase();
  try {
    if (!retainedAudioOwnerIsCurrent(owner)) throw new Error("audio_owner_changed");
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      const store = transaction.objectStore(STORE);
      const request = store.index("ownerScope").getAll(owner.scope);
      let value: T;
      request.onsuccess = () => {
        if (!retainedAudioOwnerIsCurrent(owner)) { transaction.abort(); return; }
        try { value = mutate(store, request.result as StoredRecording[]); }
        catch { transaction.abort(); }
      };
      transaction.oncomplete = () => {
        if (!retainedAudioOwnerIsCurrent(owner)) { reject(new Error("audio_owner_changed")); return; }
        resolve(value);
      };
      transaction.onerror = transaction.onabort = () => reject(new Error("audio_storage_unavailable"));
    });
  } finally { database.close(); }
}
function validStored(row: StoredRecording, now: number): boolean {
  return retainedAudioIsEligible(row, now) && row.blob instanceof Blob && row.blob.size === row.byteLength &&
    row.storageKey === `${row.ownerScope}:${row.id}` && row.blob.type === "audio/mpeg";
}
function cleanRows(store: IDBObjectStore, rows: StoredRecording[], owner: RetainedAudioOwner): StoredRecording[] {
  const now = Date.now();
  const retained = boundedRetainedAudio(rows.filter((row) => validStored(row, now)), owner.scope, now);
  const keys = new Set(retained.map((row) => row.storageKey));
  for (const row of rows) if (!keys.has(row.storageKey)) store.delete(row.storageKey);
  return retained;
}
function metadata(row: StoredRecording): RetainedAudioRecording {
  const { blob: _blob, storageKey: _key, ...result } = row;
  return result;
}
export async function listRetainedAudio(): Promise<RetainedAudioRecording[]> {
  const owner = captureRetainedAudioOwner();
  return mutateOwner(owner, (store, rows) => cleanRows(store, rows, owner).map(metadata));
}
export async function readRetainedAudio(id: string, owner = captureRetainedAudioOwner()): Promise<StoredRecording | null> {
  return mutateOwner(owner, (store, rows) => cleanRows(store, rows, owner).find((row) => row.id === id) || null);
}
export async function deleteRetainedAudio(id: string): Promise<void> {
  const owner = captureRetainedAudioOwner();
  await mutateOwner(owner, (store, rows) => {
    const row = rows.find((item) => item.id === id);
    if (row) store.delete(row.storageKey);
    clearRetainedAudioCheckpointForScope(owner.scope, id);
  });
  changed();
}
export async function deleteAllRetainedAudio(): Promise<void> {
  const owner = captureRetainedAudioOwner();
  await mutateOwner(owner, (store, rows) => { for (const row of rows) store.delete(row.storageKey); });
  clearRetainedAudioCheckpointForScope(owner.scope);
  changed();
}

/** Called only after successful account deletion with the captured deleted
 * account's stable scope, including when another account has since signed in. */
export async function deleteRetainedAudioForScope(scope: string): Promise<void> {
  clearRetainedAudioCheckpointForScope(scope);
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      const store = transaction.objectStore(STORE);
      const request = store.index("ownerScope").getAllKeys(scope);
      request.onsuccess = () => { for (const key of request.result) store.delete(key); };
      transaction.oncomplete = () => resolve();
      transaction.onerror = transaction.onabort = () => reject(new Error("audio_storage_unavailable"));
    });
  } finally { database.close(); }
  changed();
}

/** Only accepted full Dashboard MP3 plans enter this library. No section,
 * Standard preview, queue request, or device-speech clock can become a file. */
export async function retainDeliveredBriefAudio(input: {
  owner: RetainedAudioOwner; path: string; request: Record<string, unknown>; response: AudioResponse;
  blob: Blob; label: string; detail?: string; language: string; durationMs: number;
  fullBriefCompletion?: boolean;
}): Promise<RetainedAudioRecording | null> {
  if (input.path !== "/api/dashboard-briefing-audio" || !retainedAudioOwnerIsCurrent(input.owner) ||
    !attestsExactDashboardBriefingV2Plan(input.response, input.request) ||
    !input.response.audioSections || input.response.audioSections.length < (input.fullBriefCompletion === true ? 1 : 2) ||
    input.blob.type !== "audio/mpeg" || !input.blob.size || input.blob.size > RETAINED_AUDIO_MAX_BYTES ||
    !Number.isFinite(input.durationMs) || input.durationMs <= 0) return null;
  const savedAtEpochMs = Date.now();
  const digest = await crypto.subtle.digest("SHA-256", await input.blob.arrayBuffer());
  if (!retainedAudioOwnerIsCurrent(input.owner)) return null;
  const id = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
  const row: StoredRecording = {
    version: 1, id, ownerScope: input.owner.scope, kind: "brief",
    storageKey: `${input.owner.scope}:${id}`, blob: input.blob,
    label: input.label.slice(0, 240), detail: String(input.detail || "").slice(0, 240), language: input.language.slice(0, 16),
    savedAtEpochMs, expiresAtEpochMs: savedAtEpochMs + RETAINED_AUDIO_MAX_AGE_MS,
    durationMs: input.durationMs, byteLength: input.blob.size,
    sections: input.response.audioSections.map((section) => ({
      id: section.id!, title: String(section.title || "").slice(0, 240), startMs: section.startMs!, endMs: section.endMs!,
    })),
  };
  const committed = await mutateOwner(input.owner, (store, rows) => {
    // Identical accepted bytes retain their first save time, even after replay.
    if (!rows.some((item) => item.id === id)) { store.put(row); rows.push(row); }
    const retained = cleanRows(store, rows, input.owner).find((item) => item.id === id);
    return retained?.kind === "brief" ? metadata(retained) : null;
  });
  changed();
  return committed;
}

/** Retain the accepted standalone News clip as News, without Brief authority. */
export async function retainDeliveredNewsAudio(input: {
  owner: RetainedAudioOwner; path: string; blob: Blob; label: string; detail?: string; language: string; durationMs: number;
}): Promise<RetainedAudioRecording | null> {
  if (!["/api/news-story-audio", "/api/news-category-audio"].includes(input.path) || !retainedAudioOwnerIsCurrent(input.owner) ||
    input.blob.type !== "audio/mpeg" || input.blob.size < 32 || input.blob.size > RETAINED_AUDIO_MAX_BYTES ||
    !Number.isSafeInteger(input.durationMs) || input.durationMs <= 0) return null;
  const bytes = new Uint8Array(await input.blob.arrayBuffer());
  if (!(bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) && !(bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0)) return null;
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  if (!retainedAudioOwnerIsCurrent(input.owner)) return null;
  const id = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
  const savedAtEpochMs = Date.now();
  const row: StoredRecording = {
    version: 1, id, ownerScope: input.owner.scope, kind: "news", storageKey: `${input.owner.scope}:${id}`, blob: input.blob,
    label: input.label.slice(0, 240), detail: String(input.detail || "").slice(0, 240), language: input.language.slice(0, 16),
    savedAtEpochMs, expiresAtEpochMs: savedAtEpochMs + RETAINED_AUDIO_MAX_AGE_MS, durationMs: input.durationMs, byteLength: input.blob.size,
    sections: [{ id: "news", title: input.label.slice(0, 240), startMs: 0, endMs: input.durationMs }],
  };
  const committed = await mutateOwner(input.owner, (store, rows) => {
    if (!rows.some((item) => item.id === id)) { store.put(row); rows.push(row); }
    const retained = cleanRows(store, rows, input.owner).find((item) => item.id === id);
    return retained?.kind === "news" ? metadata(retained) : null;
  });
  changed();
  return committed;
}
